import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shining-technologies/ui';
import { env } from '@/lib/env';
import { useLogout } from '@/features/auth/queries';

/**
 * Idle-session timeout: after IDLE_TIMEOUT_MIN of no interaction in ANY tab,
 * warn for 60s, then auto-logout. Activity resets the timer.
 */
const WARN_SECONDS = 60;
/** Broadcast our own activity at most this often (ms) — mousemove is chatty. */
const BROADCAST_THROTTLE_MS = 5_000;
const CHANNEL_NAME = 'admin-idle';
const STORAGE_KEY = 'admin-idle-last-activity';

/**
 * FIX(admin-idle-timeout): last-activity is shared across tabs. Before: each
 * tab timed out on its own input only, so an idle background tab logged out an
 * admin who was busy in another tab (the session cookie is shared). After:
 * activity is broadcast over a BroadcastChannel (falling back to a
 * localStorage `storage` event where BroadcastChannel is unavailable) and every
 * tab counts the most recent activity from any of them.
 */
function createActivitySync(onRemote: (at: number) => void) {
  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (e: MessageEvent<unknown>) => {
      if (typeof e.data === 'number') onRemote(e.data);
    };
  }
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    const at = Number(e.newValue);
    if (Number.isFinite(at)) onRemote(at);
  };
  if (!channel) window.addEventListener('storage', onStorage);

  return {
    publish(at: number) {
      if (channel) {
        channel.postMessage(at);
        return;
      }
      try {
        window.localStorage.setItem(STORAGE_KEY, String(at));
      } catch {
        // Storage blocked — this tab still times out on its own activity.
      }
    },
    close() {
      if (channel) channel.close();
      else window.removeEventListener('storage', onStorage);
    },
  };
}

export function IdleTimeout() {
  const [warning, setWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARN_SECONDS);
  // FIX(admin-idle-timeout): depend on the stable `mutate`, not the whole
  // mutation result. Before: `[warning, logout]` — `useLogout()` returns a new
  // object every render, so the countdown interval was torn down and rebuilt on
  // every render (every tick). After: `logout` is `mutate`, stable for the
  // component's life.
  const { mutate: logout } = useLogout();

  const lastActivity = useRef(0);
  const lastBroadcast = useRef(0);
  const warningRef = useRef(false);
  const loggedOut = useRef(false);
  const sync = useRef<ReturnType<typeof createActivitySync> | null>(null);

  // FIX(admin-idle-timeout): fire logout exactly once, and never from inside a
  // state updater. Before: `logout.mutate()` ran inside `setCountdown(c => …)` —
  // a side effect in an updater (React may call it twice, and does in
  // StrictMode) — and, with the interval rebuilt every render, it kept firing
  // every second once the countdown reached 0. After: a ref guard + a plain call.
  const fireLogout = useCallback(() => {
    if (loggedOut.current) return;
    loggedOut.current = true;
    logout();
  }, [logout]);

  const markActive = useCallback((at: number, broadcast: boolean) => {
    if (at <= lastActivity.current) return;
    lastActivity.current = at;
    if (broadcast && at - lastBroadcast.current >= BROADCAST_THROTTLE_MS) {
      lastBroadcast.current = at;
      sync.current?.publish(at);
    }
  }, []);

  useEffect(() => {
    lastActivity.current = Math.max(lastActivity.current, Date.now());
    // Activity in another tab always counts — including while this tab warns.
    sync.current = createActivitySync((at) => markActive(at, false));

    const onActivity = () => {
      // While the warning is up, only an explicit "Stay signed in" counts here.
      if (!warningRef.current) markActive(Date.now(), true);
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const idleMs = env.idleTimeoutMin * 60_000;
    const tick = window.setInterval(() => {
      const idleFor = Date.now() - lastActivity.current;
      if (idleFor < idleMs) {
        if (warningRef.current) {
          warningRef.current = false;
          setWarning(false);
        }
        return;
      }
      const remaining = Math.max(0, Math.ceil((idleMs + WARN_SECONDS * 1000 - idleFor) / 1000));
      if (!warningRef.current) {
        warningRef.current = true;
        setWarning(true);
      }
      setCountdown(remaining);
      if (remaining === 0) fireLogout();
    }, 1000);

    return () => {
      window.clearInterval(tick);
      events.forEach((e) => window.removeEventListener(e, onActivity));
      sync.current?.close();
      sync.current = null;
    };
  }, [fireLogout, markActive]);

  function staySignedIn() {
    warningRef.current = false;
    setWarning(false);
    setCountdown(WARN_SECONDS);
    // Force a broadcast so other tabs showing the warning dismiss it too.
    lastBroadcast.current = 0;
    markActive(Date.now(), true);
  }

  return (
    // Not dismissible: Escape / backdrop / a corner ✕ would neither sign out nor
    // count as activity, so the only ways out are the two explicit buttons.
    <Dialog open={warning} onOpenChange={() => undefined}>
      <DialogContent hideClose>
        <DialogHeader>
          <DialogTitle>Still there?</DialogTitle>
          <DialogDescription>
            You'll be signed out in {countdown}s due to inactivity.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={fireLogout}>
            Sign out now
          </Button>
          <Button onClick={staySignedIn}>Stay signed in</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
